import { Box, Button, Menu, MenuItem, TextField } from '@mui/material';
import cx from 'classnames';
import React, { KeyboardEvent, forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { getActiveSubStage, getIsRecording, getVoiceMode } from 'store/ticket-service/selector';
import { setIsRecording, setVoiceMode } from 'store/ticket-service/ticketSlice';
import { getChoosedFile } from 'store/user-service/selector';

import { MessageSender, NamespacesList, TicketStages } from 'core/enums/common';
import { useGetUser } from 'core/hooks/useGetUser';
import useSpeechToText from 'core/hooks/useSpeechToText';
import { errorNotify } from 'core/helpers/toasts';

import { IMessage } from 'components/Chatbot/Message/types';
import { CustomTooltipElement } from 'components/shared/CustomDisabledElement/CustomTooltipElement';

import { ReactComponent as More } from 'assets/icons/more-vertical.svg';
import { ReactComponent as Send } from 'assets/icons/send.svg';

import { FilesModal } from './FilesModal';
import styles from './MessageInput.module.scss';
import * as Styled from './styled';

interface IMessageInputProps {
	placeholder?: string;
	disabled?: boolean;
	onSend: (message: IMessage) => void;
	value: string;
	setValue: (value: string) => void;
}

export const MessageInput = forwardRef<HTMLInputElement, IMessageInputProps>(
	({ onSend, placeholder = '', disabled = false, value, setValue }, ref) => {
		const { t: commonTranslate, i18n } = useTranslation(NamespacesList['common']);

		const deniedMicrophoneAccessTooltip = commonTranslate('tooltips.turnOnMicrophoneTooltip');

		const dispatch = useAppDispatch();

		const voiceMode = useSelector(getVoiceMode);
		const isRecording = useSelector(getIsRecording);
		const activeSubStage = useSelector(getActiveSubStage);

		const [permissionStatusObj, setPermissionStatusObj] = useState<PermissionStatus | null>(null);
		const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
		const [filesModal, setFilesModal] = useState(false);
		const open = Boolean(anchorEl);
		const { startRecognition, stopRecognition, result: voiceMessage } = useSpeechToText();

		const { user } = useGetUser();

		const choosedFile = useSelector(getChoosedFile);

		const handleActionFilesModal = () => {
			setFilesModal((prev) => !prev);
		};

		const handleCloseMenu = () => {
			setAnchorEl(null);
		};

		const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
			setAnchorEl(event.currentTarget);
		};

		const valueWithoutNewLines = useMemo(() => value.replace(/(\r\n|\n|\r)/gm, ''), [value]);

	const handleSend = () => {
		const message = voiceMode ? voiceMessage : value;

		if (message.trim() && !disabled) {
			// Vérifier si le message dépasse 4000 caractères (sans les retours à la ligne)
			const messageWithoutNewLines = message.replace(/(\r\n|\n|\r)/gm, '');
			if (messageWithoutNewLines.trim().length > 4000) {
				errorNotify(commonTranslate('errors.error'), commonTranslate('errors.messageLengthError'));
				return;
			}

			onSend({
				content: message.trim(),
				sender: MessageSender.user,
				sendTime: new Date().toISOString(),
				stage: TicketStages.general,
				subStage: activeSubStage,
				userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
			});
			setValue('');
		}
	};

		const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				handleSend();
			}
		};

		useEffect(() => {
			if (voiceMessage) handleSend();
		}, [voiceMessage]);

		useEffect(() => {
			if (navigator?.permissions?.query) {
				navigator.permissions
					.query({
						name: 'microphone' as PermissionName,
					})
					.then((permissionStatus) => {
						setPermissionStatusObj(permissionStatus);
					});
			}

			return () => {
				if (permissionStatusObj) {
					permissionStatusObj.onchange = null;
					setPermissionStatusObj(null);
				}
			};
		}, []);

		const isMicrophonePermissionGranted = useMemo(() => {
			if (permissionStatusObj) return permissionStatusObj.state === 'granted';
			return false;
		}, [permissionStatusObj?.state]);

		const userHasSubscription = useMemo(
			() => user?.accountPlan !== 0 && user?.accountPlan !== null,
			[user?.accountPlan]
		);

		const handleRecordingStart = () => {
			if (voiceMode) {
				stopRecognition();
			} else {
				startRecognition();
			}

			dispatch(setIsRecording(true));
			dispatch(setVoiceMode(!voiceMode));
		};

		const handleMicrophoneClick = () => {
			if (!disabled) {
				if (isMicrophonePermissionGranted) {
					handleRecordingStart();
				} else {
					navigator.mediaDevices
						.getUserMedia({ audio: true })
						.then(() => {
							handleRecordingStart();
						})
						.catch(() => {});
				}
			}
		};

		const choosedFileTooltip = useMemo(() => {
			if (choosedFile) {
				return i18n?.language === 'ar'
					? `${choosedFile.name} :${commonTranslate('tooltips.choosedFile')}`
					: `${commonTranslate('tooltips.choosedFile')}: ${choosedFile.name}`;
			}
			return commonTranslate('tooltips.chooseFileTooltip');
		}, [choosedFile, i18n?.language]);

		return (
			<>
				<Box
					sx={{
						width: '100%',
						position: 'relative',
					}}
				>
					<TextField
						size="small"
						sx={{
							'& fieldset': { border: 'none' },
							backgroundColor: '#FFFFFF',
						}}
						inputProps={{
							style: {
								padding: i18n?.language == 'ar' ? '0 0 0 25px' : '0 25px 0 0',
							},
						}}
						multiline
						ref={ref}
						placeholder={placeholder}
						className={cx(styles.message_input, 'rtl-able')}
						onKeyDown={handleKeyDown}
						onChange={(e) => setValue(e.target.value)}
						value={valueWithoutNewLines}
					/>

					<Styled.MenuButton language={i18n?.language} disabled={disabled} onClick={handleOpenMenu}>
						<More />
					</Styled.MenuButton>
					<Menu
						className="rtl-able"
						PaperProps={{
							style: {
								marginTop: '12px',
								boxShadow: '0px 12px 24px 0px rgba(40, 103, 131, 0.12)',
							},
						}}
						anchorEl={anchorEl}
						anchorOrigin={{
							vertical: 'top',
							horizontal: i18n?.language === 'ar' ? 'left' : 'right',
						}}
						transformOrigin={{
							vertical: 'bottom',
							horizontal: i18n?.language === 'ar' ? 'left' : 'right',
						}}
						open={open}
						onClose={handleCloseMenu}
						onClick={handleCloseMenu}
					>
						{userHasSubscription ? (
							<CustomTooltipElement
								message={!isMicrophonePermissionGranted ? deniedMicrophoneAccessTooltip : null}
								placement="right"
							>
								<MenuItem>
									<Styled.MicrophoneIcon
										isdisabled={disabled}
										className={`${
											voiceMode ? (isRecording ? styles.pulse_active : styles.pulse_inactive) : ''
										} ${
											voiceMode
												? isRecording
													? styles.microphone_active
													: styles.microphone_inactive
												: ''
										}`}
										onClick={handleMicrophoneClick}
									/>
								</MenuItem>
							</CustomTooltipElement>
						) : (
							<CustomTooltipElement
								message={commonTranslate('tooltips.unavailableForYourSubscriptionLevel')}
								placement="right"
							>
								<MenuItem
									sx={{
										cursor: 'default',
										'&:hover': {
											backgroundColor: 'transparent',
										},
									}}
								>
									<Styled.MicrophoneIcon isdisabled={true} style={{ cursor: 'default' }} />
								</MenuItem>
							</CustomTooltipElement>
						)}
						{userHasSubscription ? (
							<CustomTooltipElement message={choosedFileTooltip} placement="right">
								<MenuItem onClick={handleActionFilesModal}>
									<Styled.DocumentIcon choosedfile={!!choosedFile} isdisabled={disabled} />
								</MenuItem>
							</CustomTooltipElement>
						) : (
							<CustomTooltipElement
								message={commonTranslate('tooltips.unavailableForYourSubscriptionLevel')}
								placement="right"
							>
								<MenuItem
									sx={{
										cursor: 'default',
										'&:hover': {
											backgroundColor: 'transparent',
										},
									}}
								>
									<Styled.DocumentIcon choosedfile={false} isdisabled={true} style={{ cursor: 'default' }} />
								</MenuItem>
							</CustomTooltipElement>
						)}
					</Menu>
				</Box>
				<Button
					data-testid={'chat-message-send'}
					sx={{
						textTransform: 'uppercase',
						minWidth: '44px',
						width: '44px',
						height: '44px',
						borderRadius: '8px',
						padding: '0',
					}}
					type="button"
					onClick={() => handleSend()}
					disabled={disabled}
					variant="contained"
					color="primary"
				>
					<Send />
				</Button>
				<FilesModal isOpen={filesModal} onClose={handleActionFilesModal} />
			</>
		);
	}
);
